import React from "react";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Facebook, 
  Twitter, 
  Linkedin, 
  Youtube,
  ExternalLink,
  Building2,
  Users,
  Zap,
  Shield
} from "lucide-react";

const Footer = () => {
  const footerLinks = {
    "WCD Initiatives": [
      { label: "Malnutrition Prediction", href: "/ai-services/malnutrition-prediction" },
      { label: "Face Recognition System", href: "/ai-services/face-recognition-system" },
      { label: "Resource Allocation AI", href: "/ai-services/resource-allocation-ai" },
      { label: "Helpline Assistant", href: "/ai-services/helpline-assistant" }
    ],
    "Government": [
      { label: "Gujarat.gov.in", href: "https://www.gujaratindia.gov.in/", external: true },
      { label: "WCD Gujarat", href: "https://wcd.gujarat.gov.in/", external: true },
      { label: "Digital India", href: "https://www.digitalindia.gov.in/", external: true },
      { label: "India.gov.in", href: "https://www.india.gov.in/", external: true }
    ],
    "Resources": [
      { label: "WCD Dashboard", href: "#dashboard" },
      { label: "Gujarat Map", href: "#map" },
      { label: "Schemes & Updates", href: "#news" },
      { label: "Partnership Portal", href: "#partnerships" }
    ],
    "Support": [
      { label: "Help Center", href: "/help" },
      { label: "181 Helpline", href: "tel:181" },
      { label: "Contact Us", href: "/contact" },
      { label: "Privacy Policy", href: "/privacy" }
    ]
  };

  const quickStats = [
    { icon: Building2, label: "Anganwadi Centers", value: "53,065" },
    { icon: Users, label: "Women Beneficiaries", value: "16.49L" },
    { icon: Zap, label: "Children Served", value: "7.68L" },
    { icon: Shield, label: "Districts Covered", value: "33" }
  ];

  const handleLinkClick = (href, external) => {
    if (external) {
      window.open(href, '_blank');
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Quick Stats Bar */}
      <div className="border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {quickStats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <stat.icon className="w-6 h-6" />
                </div>
                <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">G</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold">Gujarat WCD</h3>
                <p className="text-gray-400">Women & Child Development</p>
              </div>
            </div>
            
            <p className="text-gray-300 leading-relaxed max-w-md">
              Gujarat's premier Women & Child Development department, empowering women and ensuring child 
              development through innovative AI-driven governance and comprehensive welfare programs.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-gray-300">
                <MapPin className="w-5 h-5 text-pink-500 flex-shrink-0" />
                <span className="text-sm">Block No. 5, 2nd Floor, Dr. Jivraj Mehta Bhavan, Gandhinagar 382010</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <Phone className="w-5 h-5 text-pink-500 flex-shrink-0" />
                <span className="text-sm">181 (Mahila Helpline) | +91 79 2325 4400</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <Mail className="w-5 h-5 text-pink-500 flex-shrink-0" />
                <span className="text-sm">wcd-guj@gujarat.gov.in</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              {[
                { icon: Twitter, href: "#", label: "Twitter" },
                { icon: Facebook, href: "#", label: "Facebook" },
                { icon: Linkedin, href: "#", label: "LinkedIn" },
                { icon: Youtube, href: "#", label: "YouTube" }
              ].map((social, index) => (
                <Button
                  key={index}
                  variant="ghost"
                  size="sm"
                  className="w-10 h-10 rounded-full border border-slate-700 hover:border-pink-500 hover:bg-pink-500/10 text-gray-400 hover:text-pink-400 transition-all duration-300"
                  onClick={() => handleLinkClick(social.href, true)}
                >
                  <social.icon className="w-5 h-5" />
                </Button>
              ))}
            </div>
          </div>

          {/* Links Sections */}
          <div className="lg:col-span-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {Object.entries(footerLinks).map(([title, links]) => (
              <div key={title} className="space-y-4">
                <h4 className="text-lg font-semibold text-white">{title}</h4>
                <ul className="space-y-3">
                  {links.map((link, index) => (
                    <li key={index}>
                      <button
                        onClick={() => handleLinkClick(link.href, link.external)}
                        className="text-gray-400 hover:text-pink-400 transition-colors text-sm flex items-center group"
                      >
                        {link.label}
                        {link.external && (
                          <ExternalLink className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <Separator className="my-12 bg-slate-700" />

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-gray-400 text-sm">
            © 2025 Gujarat Women & Child Development Department. All rights reserved.
          </div>
          
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
            <button className="hover:text-pink-400 transition-colors">
              Terms of Service
            </button>
            <button className="hover:text-pink-400 transition-colors">
              Privacy Policy
            </button>
            <button className="hover:text-pink-400 transition-colors">
              Cookie Policy
            </button>
            <button className="hover:text-pink-400 transition-colors">
              Accessibility
            </button>
          </div>
        </div>

        {/* Government Disclaimer */}
        <div className="mt-8 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
          <p className="text-xs text-gray-400 text-center leading-relaxed">
            This is an official website of the Government of Gujarat, India. 
            All information provided is authentic and verified. For women's safety, call 181 helpline. 
            For the best experience, please use the latest version of modern browsers.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;